import React from 'react';
import {
  LayoutDashboard,
  QrCode,
  Settings,
  LogOut,
  Menu,
  X,
  ShieldAlert,
  ClipboardList, FileUp, Printer, BarChart3 } from 'lucide-react';
import { ViewState } from '../types';
import { signOut } from '../services/supabase';

interface ManagerLayoutProps {
  children: React.ReactNode;
  /** 運営者のみ「機能利用状況」をサイドバーに出す。
   *  出さなくても RPC 側で 42501 になるので、これは表示上の配慮にすぎない。 */
  isOperator?: boolean;
  /** 中身がA4紙面を印刷する画面のとき true。
   *
   *  このレイアウトは h-screen + overflow-hidden / main の overflow-y-auto で
   *  画面1枚に収める作りなので、そのまま印刷すると紙面が画面の高さで切られる。
   *  PosterSheetStyles の @media print が高さと overflow を戻せるよう ap-print-reset を付け、
   *  サイドバー等の画面用UIは ap-no-print で落とす。 */
  printable?: boolean;
  currentView: ViewState;
  onChangeView: (view: ViewState) => void;
}

export const ManagerLayout: React.FC<ManagerLayoutProps> = ({
  children,
  currentView,
  onChangeView,
  printable = false,
  isOperator = false,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // モバイルメニューを Esc で閉じる
  React.useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileMenuOpen]);

  const NavItem = ({ view, icon: Icon, label }: { view: ViewState, icon: any, label: string }) => (
    <button
      onClick={() => {
        onChangeView(view);
        setIsMobileMenuOpen(false);
      }}
      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
        currentView === view 
          ? 'bg-safety-500 text-white shadow-md' 
          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
      }`}
    >
      <Icon size={20} />
      <span className="font-medium">{label}</span>
    </button>
  );

  const printReset = printable ? ' ap-print-reset' : '';
  const printHide = printable ? ' ap-no-print' : '';

  return (
    <div className={`flex h-screen bg-slate-100 overflow-hidden${printReset}`}>
      {/* Sidebar Desktop */}
      <aside
        className={`hidden md:flex w-64 flex-col bg-industrial-900 text-white shadow-xl z-20${printHide}`}
      >
        <div className="p-6 flex items-center space-x-2 border-b border-slate-700">
          <img src="/icon-512x512.png" alt="" className="h-9 w-9 rounded-md" />
          <div>
            <h1 className="font-bold text-lg leading-tight">安全ポスト＋</h1>
            <p className="text-xs text-slate-400">Manager Portal</p>
          </div>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2">
          <NavItem view="manager_dashboard" icon={LayoutDashboard} label="ダッシュボード" />
          <NavItem view="manager_cases" icon={ClipboardList} label="ケース一覧" />
          <NavItem view="manager_import" icon={FileUp} label="紙から取り込み" />
          <NavItem view="manager_qr" icon={QrCode} label="QR管理" />
          <NavItem view="posters" icon={Printer} label="掲示ポスター" />
          <NavItem view="manager_settings" icon={Settings} label="設定・プラン" />
          {isOperator && (
            <>
              <div className="pt-3 mt-3 border-t border-slate-700">
                <p className="px-4 pb-1 text-[10px] font-bold tracking-wider text-slate-500">運営者</p>
              </div>
              <NavItem view="operator_usage" icon={BarChart3} label="機能利用状況" />
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <button
            onClick={async () => {
              await signOut();
              onChangeView('landing');
            }}
            className="flex items-center space-x-3 px-4 py-3 text-slate-400 hover:text-white transition-colors w-full"
          >
            <LogOut size={20} />
            <span>ログアウト</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header: メニューオーバーレイ(z-40)より上、モーダル(z-50)より下に置く。
          以前は z-30 でオーバーレイの下に隠れ、開くとハンバーガーが押せなくなっていた。 */}
      <div
        className={`md:hidden fixed top-0 w-full bg-industrial-900 text-white z-[45] flex items-center justify-between p-4 shadow-md${printHide}`}
      >
        <div className="flex items-center space-x-2">
          <img src="/icon-512x512.png" alt="" className="h-6 w-6 rounded" />
          <span className="font-bold">安全ポスト＋</span>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2.5 -m-2.5"
          aria-expanded={isMobileMenuOpen}
          aria-label={isMobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay: 空白部タップでも閉じる（NavItem 自身も閉じるのでバブリングは無害） */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-industrial-900 z-40 pt-20 px-6 space-y-4 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <NavItem view="manager_dashboard" icon={LayoutDashboard} label="ダッシュボード" />
          <NavItem view="manager_cases" icon={ClipboardList} label="ケース一覧" />
          <NavItem view="manager_import" icon={FileUp} label="紙から取り込み" />
          <NavItem view="manager_qr" icon={QrCode} label="QR管理" />
          <NavItem view="posters" icon={Printer} label="掲示ポスター" />
          <NavItem view="manager_settings" icon={Settings} label="設定・プラン" />
          {isOperator && (
            <>
              <div className="pt-3 mt-3 border-t border-slate-700">
                <p className="px-4 pb-1 text-[10px] font-bold tracking-wider text-slate-500">運営者</p>
              </div>
              <NavItem view="operator_usage" icon={BarChart3} label="機能利用状況" />
            </>
          )}
          <button
            onClick={async () => {
              await signOut();
              onChangeView('landing');
            }}
            className="flex items-center space-x-3 px-4 py-3 text-slate-400 hover:text-white w-full border-t border-slate-700 mt-4 pt-4"
          >
            <LogOut size={20} />
            <span>ログアウト</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 overflow-y-auto pt-16 md:pt-0 bg-slate-50${printReset}`}>
        <div className={`p-4 md:p-8 max-w-7xl mx-auto min-h-full${printReset}`}>
          {children}
        </div>
      </main>
    </div>
  );
};

export const WorkerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 二重スマホフレーム回避:
  //  - md 以上 (デスクトップ): 従来通りスマホ・モックアップ枠 (status bar / notch / home indicator) を表示
  //  - md 未満 (モバイル実機): 枠・モック要素を非表示にし、アプリヘッダー + コンテンツだけ全幅で表示
  return (
    <div className="w-full md:max-w-[375px] mx-auto">
      {/* Phone frame: 角丸・影・幅制限はデスクトップ専用 */}
      <div className="bg-white md:rounded-[2rem] md:shadow-2xl md:overflow-hidden md:ring-1 md:ring-slate-200">
        {/* Status bar (9:41 + ノッチ): デスクトップのモックアップ専用 */}
        <div className="hidden md:flex bg-industrial-900 pt-3 pb-1 px-6 justify-between items-center">
          <span className="text-white text-[10px]">9:41</span>
          <div className="w-20 h-5 bg-black rounded-full" />
          <div className="flex items-center gap-1">
            <div className="w-3.5 h-2 border border-white rounded-sm relative">
              <div className="absolute inset-0.5 bg-white rounded-[1px]" />
            </div>
          </div>
        </div>
        {/* App header: 常時表示. デスクトップでは status bar 直下、モバイルでは画面トップ. */}
        <div className="bg-industrial-900 px-4 py-3 md:pb-3 md:pt-0 flex justify-center items-center space-x-2">
           <img src="/icon-512x512.png" alt="" className="h-5 w-5 rounded" />
           <span className="text-white font-bold text-base">安全ポスト＋</span>
        </div>
        {/* Content: モバイル時は padding を少し小さめに */}
        <div className="p-4 md:p-5 md:min-h-[520px]">
          {children}
        </div>
        {/* Home indicator: デスクトップのモックアップ専用 */}
        <div className="hidden md:flex pb-2 justify-center">
          <div className="w-28 h-1 bg-slate-300 rounded-full" />
        </div>
      </div>
      {/* "Powered by" バッジ: モバイル実機では UI ノイズになるので非表示 */}
      <p className="hidden md:block mt-4 text-xs text-slate-400 text-center">
        Powered by 安全ポスト＋<br/>
        安全第一・整理整頓
      </p>
    </div>
  );
};